import { useState } from "react";
import { AnyObject } from "yup";

export const useMakeRequest = () => {
    const [requestPending, setRequestPending] = useState(false);

    const makeRequest = <T>(request: () => Promise<T>): Promise<T> => {
        setRequestPending(true);
        return request()
            .finally(() => {
                setRequestPending(false);
            });
    };

    return { requestPending, makeRequest };
};

export const useParallelMakeRequests = <TObject extends AnyObject>() => {
    const [requestPending, setRequestPending] = useState<{ [key in keyof TObject]?: boolean }>({});

    const makeParallelRequest = <T>(
        request: () => Promise<T>,
        key: keyof TObject
    ): Promise<T> => {
        setRequestPending(prev => ({ ...prev, [key]: true }));
        return request()
            .finally(() => {
                setRequestPending(prev => ({ ...prev, [key]: false }));
            });
    };

    return { requestPending, makeParallelRequest };
};

export const useStateMakeRequest = <T, T2>(
    initial: T,
    action: () => Promise<T2>
) => {
    const [requestPending, setRequestPending] = useState(false);
    const [state, setState] = useState<T>(initial);

    const makeRequest = async (): Promise<T2> => {
        setRequestPending(true);
        try {
            const result = await action();
            return result; // Return the result of the action
        } finally {
            setRequestPending(false);
        }
    };

    return [makeRequest, state, setState, requestPending] as const;
};
